import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { formatPrice } from '@/lib/utils'
import { AnimatedPage, AnimatedPageHeader } from '@components/animated'
import { apartmentAnalyticsProps, trackEvent } from '@services/livability/analytics'
import { getApartments } from '@services/livability/apartments'
import { getApartmentThumbnailUrl } from '@services/livability/images'
import { useApartmentFilterOptions } from '@services/livability/queries/useApartmentFilterOptions'
import type { Apartment, ApartmentListResponse } from '@services/livability/types'
import {
  ChevronLeft,
  ChevronRight,
  DoorOpen,
  Home,
  MapPin,
  Maximize2,
  Search
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ApartmentThumbnail } from '../components/ApartmentThumbnail'
import { ApartmentViewSwitcher } from '../components/ApartmentViewSwitcher'
import { FilterChipBar } from '../components/filters/FilterChipBar'
import { ListingModeToggle } from '../components/ListingModeToggle'
import { useApartmentFilters } from '../hooks/useApartmentFilters'
import {
  buildFiltersAppliedEventProps,
  type ApartmentFilters
} from '../utils/apartmentFilterUrl'
import { useLivabilityPaths } from '../utils/paths'

const EMPTY_RESULT: ApartmentListResponse = {
  items: [],
  total: 0,
  limit: 24,
  offset: 0,
  page: 1
}

const FALLBACK_SORT_OPTIONS = [
  { value: 'price_asc', label: 'Cena: Najnižšia' },
  { value: 'price_desc', label: 'Cena: Najvyššia' },
  { value: 'area_asc', label: 'Plocha: Najmenšia' },
  { value: 'area_desc', label: 'Plocha: Najväčšia' },
  { value: 'newest', label: 'Najnovšie' }
]
const SORT_LABELS: Record<string, string> = Object.fromEntries(
  FALLBACK_SORT_OPTIONS.map((option) => [option.value, option.label])
)

export function Apartments() {
  const { t } = useTranslation('livability')
  const paths = useLivabilityPaths()
  const searchBarRef = useRef<HTMLDivElement>(null)
  const [result, setResult] = useState<ApartmentListResponse>(EMPTY_RESULT)
  const [draftQ, setDraftQ] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const {
    filters,
    q,
    page,
    limit,
    listingType,
    apiParams,
    setFilters,
    clearFilters,
    setQ,
    setPage
  } = useApartmentFilters()

  const { data: filterOptions } = useApartmentFilterOptions(listingType)
  const sortOptions =
    filterOptions?.sort_options.map((value) => ({
      value,
      label: SORT_LABELS[value] ?? value
    })) ?? FALLBACK_SORT_OPTIONS

  useEffect(() => {
    setDraftQ(q)
  }, [q])

  useEffect(() => {
    const controller = new AbortController()

    async function load() {
      setIsLoading(true)
      try {
        const data = await getApartments(
          apiParams,
          listingType,
          controller.signal
        )
        setResult(data)
        setError(null)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError(
          err instanceof Error ? err.message : 'Failed to load apartments'
        )
      } finally {
        if (!controller.signal.aborted) setIsLoading(false)
      }
    }

    load()
    return () => {
      controller.abort()
    }
  }, [apiParams, listingType])

  const totalPages = Math.max(1, Math.ceil(result.total / result.limit))
  const startIndex =
    result.total === 0 ? 0 : (result.page - 1) * result.limit + 1
  const endIndex = Math.min(result.page * result.limit, result.total)

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage)
    searchBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleFiltersChange = (nextFilters: ApartmentFilters) => {
    setFilters(nextFilters, { resetPage: true })
    trackEvent(
      'filters_applied',
      buildFiltersAppliedEventProps('list', listingType, nextFilters, {
        q,
        page: 1,
        limit
      })
    )
  }

  const handleClearFilters = () => {
    clearFilters()
    trackEvent(
      'filters_applied',
      buildFiltersAppliedEventProps('list', listingType, filters, { cleared: true })
    )
  }

  const handleSortChange = (sortBy: string) => {
    handleFiltersChange({ ...filters, sortBy })
  }

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setQ(draftQ)
    trackEvent(
      'filters_applied',
      buildFiltersAppliedEventProps('list', listingType, filters, {
        q: draftQ,
        page: 1,
        limit
      })
    )
  }

  if (isLoading && result.items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Načítavanie bytov...</p>
        </div>
      </div>
    )
  }

  if (error && result.items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">Chyba</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <AnimatedPage className="app-chrome space-y-6">
      <AnimatedPageHeader
        title={t('apartments.pageTitle')}
        description={t('apartments.pageDescription')}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <ApartmentViewSwitcher />
          <ListingModeToggle />
        </div>
        {isLoading && (
          <span className="text-sm text-muted-foreground">Obnovujem…</span>
        )}
      </div>

      <FilterChipBar
        filters={filters}
        filterOptions={filterOptions}
        onChange={handleFiltersChange}
        onClearAll={handleClearFilters}
      />

      <div className="scroll-mt-20 space-y-3" ref={searchBarRef}>
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Vyhľadať byty podľa názvu, lokality..."
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              className="pl-10"
            />
          </form>
          <Select value={filters.sortBy ?? 'price_asc'} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px] shrink-0">
              <SelectValue placeholder="Zoradiť podľa" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">
          Zobrazených: {startIndex}-{endIndex} z {result.total}{' '}
          {result.total === 1
            ? 'bytu'
            : result.total < 5
              ? 'bytov'
              : 'bytov'}
        </p>
      </div>

      {result.items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Home className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {q ? 'Žiadne výsledky' : 'Zatiaľ žiadne byty'}
            </h3>
            <p className="text-muted-foreground text-center">
              {q
                ? 'Skúste iný vyhľadávací výraz alebo filtre'
                : 'V databáze zatiaľ nie sú žiadne byty'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {result.items.map((apartment: Apartment) => (
              <Card
                key={apartment.id}
                className="hover:shadow-lg transition-shadow flex flex-col overflow-hidden"
              >
                <ApartmentThumbnail
                  src={getApartmentThumbnailUrl(apartment)}
                  alt={apartment.title}
                  className="rounded-none rounded-t-lg"
                />
                <CardHeader>
                  <CardTitle className="line-clamp-1">
                    {apartment.title}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {apartment.location}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="flex-1">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-primary">
                          {formatPrice(apartment.price_month)}
                        </span>
                        {listingType === 'rent' &&
                          apartment.price_month > 0 && (
                            <span className="text-sm text-muted-foreground">
                              /mesiac
                            </span>
                          )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Maximize2 className="h-4 w-4 text-muted-foreground" />
                          <span>{apartment.area_m2} m²</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DoorOpen className="h-4 w-4 text-muted-foreground" />
                          <span>{apartment.room_count} izby</span>
                        </div>
                      </div>

                      {apartment.floor && (
                        <div className="text-sm text-muted-foreground">
                          Poschodie: {apartment.floor}
                        </div>
                      )}

                      {apartment.condition && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Stav:</span>{' '}
                          {apartment.condition}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t mt-5">
                    <Button asChild variant="outline" className="w-full">
                      <Link
                        to={paths.detail(apartment.id)}
                        onClick={() =>
                          trackEvent(
                            'apartment_clicked',
                            apartmentAnalyticsProps(apartment, 'list')
                          )
                        }
                      >
                        Zobraziť detail
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Predchádzajúca
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    return (
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - page) <= 1
                    )
                  })
                  .map((p, index, array) => (
                    <div key={p} className="flex items-center">
                      {index > 0 && array[index - 1] !== p - 1 && (
                        <span className="px-2 text-muted-foreground">...</span>
                      )}
                      <Button
                        variant={page === p ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(p)}
                        className="min-w-[2.5rem]"
                        disabled={isLoading}
                      >
                        {p}
                      </Button>
                    </div>
                  ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || isLoading}
              >
                Nasledujúca
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </AnimatedPage>
  )
}
